import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useCreatePerson, useUpdatePerson } from '@/hooks/usePersons';
import { useCreateFamilyMember } from '@/hooks/useFamilyMembers';
import { useTreeStore } from '@/store/useTreeStore';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { PersonAutocomplete } from './PersonAutocomplete';
import type { PersonDetail } from '@/api/persons.api';

// ─── Zod-Schema ────────────────────────────────────────────────────────────

const personFormSchema = z.object({
  title: z.string().max(100).optional().or(z.literal('')),
  firstName: z.string().min(1, 'Vorname ist erforderlich.').max(100),
  lastName: z.string().max(100).optional().or(z.literal('')),
  birthName: z.string().max(100).optional().or(z.literal('')),
  gender: z.enum(['male', 'female', 'other', 'unknown']).optional(),
  birthDateText: z.string().max(50).optional().or(z.literal('')),
  birthPlace: z.string().max(200).optional().or(z.literal('')),
  isLiving: z.boolean(),
  deathDateText: z.string().max(50).optional().or(z.literal('')),
  deathPlace: z.string().max(200).optional().or(z.literal('')),
  occupation: z.string().max(200).optional().or(z.literal('')),
  notes: z.string().max(5000).optional().or(z.literal('')),
  // Familie (nur bei Erstellen)
  parent1Id: z.string().nullable().optional(),
  parent2Id: z.string().nullable().optional(),
});

type PersonFormValues = z.infer<typeof personFormSchema>;

// ─── Props ─────────────────────────────────────────────────────────────────

interface PersonFormProps {
  /** Null = neue Person, PersonDetail = bearbeiten */
  person?: PersonDetail | null;
  /** Schließt das Modal */
  onClose: () => void;
}

// ─── PersonForm ────────────────────────────────────────────────────────────

export function PersonForm({ person, onClose }: PersonFormProps) {
  const isEdit = !!person;
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  const createFamilyMember = useCreateFamilyMember();
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);
  const confirm = useConfirm();

  // ─── Standardwerte ────────────────────────────────────────────────────

  const defaultValues = useMemo<PersonFormValues>(
    () => ({
      title: person?.title ?? '',
      firstName: person?.firstName ?? '',
      lastName: person?.lastName ?? '',
      birthName: person?.birthName ?? '',
      gender: (person?.gender as PersonFormValues['gender']) ?? 'unknown',
      birthDateText: person?.birthDateText ?? '',
      birthPlace: person?.birthPlace ?? '',
      isLiving: person?.isLiving ?? true,
      deathDateText: person?.deathDateText ?? '',
      deathPlace: person?.deathPlace ?? '',
      occupation: person?.occupation ?? '',
      notes: person?.notes ?? '',
      parent1Id: null,
      parent2Id: null,
    }),
    [person],
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<PersonFormValues>({
    resolver: zodResolver(personFormSchema),
    defaultValues,
  });

  // Formular zurücksetzen wenn sich person ändert
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const isLiving = watch('isLiving');

  // ─── Schließen mit Dirty-Guard ────────────────────────────────────────
  // Backdrop-Klick/ESC sollen minutenlange Eingaben nicht kommentarlos
  // verwerfen. Nach erfolgreichem Submit wird onClose direkt aufgerufen.

  const requestClose = useCallback(async () => {
    if (isSubmitting) return;
    if (isDirty) {
      const ok = await confirm({
        title: 'Eingaben verwerfen?',
        message: 'Nicht gespeicherte Änderungen gehen verloren.',
        confirmLabel: 'Verwerfen',
        cancelLabel: 'Weiter bearbeiten',
        variant: 'default',
      });
      if (!ok) return;
    }
    onClose();
  }, [confirm, isDirty, isSubmitting, onClose]);

  // ─── Absenden ─────────────────────────────────────────────────────────

  const onSubmit = async (values: PersonFormValues) => {
    // DTO bauen (snake_case für API).
    // Edit: leere Felder als null senden, damit sie in der DB geleert werden
    // (undefined wird von JSON.stringify entfernt → Feld bliebe unverändert).
    const empty = isEdit ? null : undefined;
    const dto = {
      title: values.title || empty,
      first_name: values.firstName,
      last_name: values.lastName || empty,
      birth_name: values.birthName || empty,
      gender: values.gender || undefined,
      birth_date_text: values.birthDateText || empty,
      birth_place: values.birthPlace || empty,
      is_living: values.isLiving,
      // Lebt die Person (wieder), werden Sterbedaten geleert statt behalten
      death_date_text: !values.isLiving ? (values.deathDateText || empty) : empty,
      death_place: !values.isLiving ? (values.deathPlace || empty) : empty,
      occupation: values.occupation || empty,
      notes: values.notes || empty,
    };

    try {
      if (isEdit && person) {
        // ─── Aktualisieren ────────────────────────────────────────────
        await updatePerson.mutateAsync({ id: person.id, dto });
        onClose();
      } else {
        // ─── Erstellen ────────────────────────────────────────────────
        const created = await createPerson.mutateAsync(dto);

        // Eltern zuordnen falls ausgewählt. Schlägt NUR dieser Schritt fehl,
        // wird das Modal trotzdem geschlossen – sonst legt ein erneuter
        // Submit die Person doppelt an.
        const p1 = values.parent1Id;
        const p2 = values.parent2Id;
        if (p1 || p2) {
          try {
            await createFamilyMember.mutateAsync({
              child_id: created.id,
              parent1_id: p1 || undefined,
              parent2_id: p2 || undefined,
              relation_type: 'biological',
            });
          } catch {
            toast.error(
              'Person wurde angelegt, aber die Eltern-Zuordnung ist fehlgeschlagen. ' +
                'Bitte über "Beziehungen" nachtragen.',
            );
          }
        }

        // Person im Tree auswählen und Sidebar öffnen
        setSelectedPerson(created.id);
        onClose();
      }
    } catch {
      // Fehler werden bereits durch die Hooks (toast) behandelt
    }
  };

  // ─── ESC schließt Modal (mit Dirty-Guard) ─────────────────────────────

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') requestClose();
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [requestClose]);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={requestClose}>
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit
              ? `${[person!.title, person!.firstName, person!.lastName].filter(Boolean).join(' ')} bearbeiten`.trim()
              : 'Neue Person anlegen'}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Schließen"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Formular */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 px-4 py-4 sm:space-y-6 sm:px-6 sm:py-5">
          {/* ═══ Grunddaten ══════════════════════════════════════════════════ */}
          <fieldset>
            <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Grunddaten
            </legend>
            <div className="space-y-3">
              {/* Titel */}
              <div>
                <label htmlFor="pf-title" className="mb-1 block text-sm font-medium text-slate-700">
                  Titel
                </label>
                <input
                  id="pf-title"
                  type="text"
                  {...register('title')}
                  placeholder="z. B. Dr., Prof., Pfr."
                  className={inputClass(false)}
                />
              </div>

              {/* Vorname */}
              <div>
                <label htmlFor="pf-firstName" className="mb-1 block text-sm font-medium text-slate-700">
                  Vorname <span className="text-red-500">*</span>
                </label>
                <input
                  id="pf-firstName"
                  type="text"
                  {...register('firstName')}
                  className={inputClass(!!errors.firstName)}
                  autoFocus
                />
                {errors.firstName && <FieldError message={errors.firstName.message} />}
              </div>

              {/* Nachname */}
              <div>
                <label htmlFor="pf-lastName" className="mb-1 block text-sm font-medium text-slate-700">
                  Nachname
                </label>
                <input id="pf-lastName" type="text" {...register('lastName')} className={inputClass(false)} />
              </div>

              {/* Geburtsname */}
              <div>
                <label htmlFor="pf-birthName" className="mb-1 block text-sm font-medium text-slate-700">
                  Geburtsname
                </label>
                <input id="pf-birthName" type="text" {...register('birthName')} className={inputClass(false)} />
              </div>

              {/* Geschlecht */}
              <div>
                <label htmlFor="pf-gender" className="mb-1 block text-sm font-medium text-slate-700">
                  Geschlecht
                </label>
                <select id="pf-gender" {...register('gender')} className={selectClass}>
                  <option value="unknown">Unbekannt</option>
                  <option value="male">Männlich</option>
                  <option value="female">Weiblich</option>
                  <option value="other">Divers</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* ═══ Geburt ═════════════════════════════════════════════════════ */}
          <fieldset>
            <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Geburt
            </legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="pf-birthDateText" className="mb-1 block text-sm font-medium text-slate-700">
                  Geburtsdatum
                </label>
                <input
                  id="pf-birthDateText"
                  type="text"
                  {...register('birthDateText')}
                  placeholder="z.B. 15.03.1985"
                  className={inputClass(false)}
                />
              </div>
              <div>
                <label htmlFor="pf-birthPlace" className="mb-1 block text-sm font-medium text-slate-700">
                  Geburtsort
                </label>
                <input
                  id="pf-birthPlace"
                  type="text"
                  {...register('birthPlace')}
                  placeholder="z.B. München"
                  className={inputClass(false)}
                />
              </div>
            </div>
          </fieldset>

          {/* ═══ Tod ════════════════════════════════════════════════════════ */}
          <fieldset>
            <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Lebensstatus
            </legend>

            {/* Checkbox „lebt noch" */}
            <label className="mb-3 flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isLiving')}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Person lebt noch</span>
            </label>

            {/* Sterbedaten (nur wenn verstorben) */}
            {!isLiving && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="pf-deathDateText" className="mb-1 block text-sm font-medium text-slate-700">
                    Sterbedatum
                  </label>
                  <input
                    id="pf-deathDateText"
                    type="text"
                    {...register('deathDateText')}
                    placeholder="z.B. 22.11.2020"
                    className={inputClass(false)}
                  />
                </div>
                <div>
                  <label htmlFor="pf-deathPlace" className="mb-1 block text-sm font-medium text-slate-700">
                    Sterbeort
                  </label>
                  <input
                    id="pf-deathPlace"
                    type="text"
                    {...register('deathPlace')}
                    placeholder="z.B. Berlin"
                    className={inputClass(false)}
                  />
                </div>
              </div>
            )}
          </fieldset>

          {/* ═══ Weitere Angaben ════════════════════════════════════════════ */}
          <fieldset>
            <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Weitere Angaben
            </legend>
            <div className="space-y-3">
              <div>
                <label htmlFor="pf-occupation" className="mb-1 block text-sm font-medium text-slate-700">
                  Beruf
                </label>
                <input id="pf-occupation" type="text" {...register('occupation')} className={inputClass(false)} />
              </div>
              <div>
                <label htmlFor="pf-notes" className="mb-1 block text-sm font-medium text-slate-700">
                  Notizen
                </label>
                <textarea
                  id="pf-notes"
                  {...register('notes')}
                  rows={3}
                  className={inputClass(false)}
                />
              </div>
            </div>
          </fieldset>

          {/* ═══ Familie (nur bei Erstellen) ════════════════════════════════ */}
          {!isEdit && (
            <fieldset>
              <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                Familie (optional)
              </legend>
              <p className="mb-3 text-xs text-slate-400">
                Eltern können direkt beim Erstellen zugeordnet werden. Weitere Beziehungen lassen sich danach über die
                Personendetails verwalten.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Elternteil 1</label>
                  <Controller
                    name="parent1Id"
                    control={control}
                    render={({ field }) => (
                      <PersonAutocomplete
                        value={field.value ?? null}
                        onChange={field.onChange}
                        placeholder="Elternteil suchen..."
                        aria-label="Elternteil 1"
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Elternteil 2</label>
                  <Controller
                    name="parent2Id"
                    control={control}
                    render={({ field }) => (
                      <PersonAutocomplete
                        value={field.value ?? null}
                        onChange={field.onChange}
                        placeholder="Elternteil suchen..."
                        aria-label="Elternteil 2"
                      />
                    )}
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* ═══ Aktions-Buttons ════════════════════════════════════════════ */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors
                         hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                         disabled:opacity-50"
            >
              {isSubmitting
                ? 'Speichern...'
                : isEdit
                  ? 'Änderungen speichern'
                  : 'Person anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Hilfsfunktionen ───────────────────────────────────────────────────────

function inputClass(hasError: boolean): string {
  return `w-full rounded border ${
    hasError ? 'border-red-400' : 'border-slate-300'
  } bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400
    focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400`;
}

const selectClass = `w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
  focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400`;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-500">{message}</p>;
}
